import { useEffect, useState, useRef, useImperativeHandle, forwardRef } from "react";
import { FrozenDrop } from "./RainEndPhase";

interface RainDrop {
  id: string;
  x: number;
  drift: number;
  delay: number;
  duration: number;
  rotation: number;
}

interface RainDropsProps {
  isActive: boolean;
  animationSpeed?: number;
}

export interface RainDropsHandle {
  getPositions: () => FrozenDrop[];
}

/**
 * Ennyi espont esik egyszerre. Ez a suruseg hangoloszama.
 *
 * Korabban 20-rol indult es egy 200 ms-os intervallum toltotte fel 50-ig. Az
 * intervallum minden lepesnel ujrarenderelte a TELJES listat, tehat 50 elem
 * kapott uj React-fat masodpercenkent otszor -- feleslegesen, mert a mozgast
 * ugyis az animacio adja. Most egyszer generalodik, negativ keslessel elosztva,
 * igy az elso pillanattol tele van a keret, es nincs tobb idozitett render.
 */
const DROP_COUNT = 36;

/**
 * A cseppek animacioja CSS, nem JS.
 *
 * Az elozo valtozat 50 framer-motion animaciot futtatott egyszerre; azok a fo
 * szalon, minden kepkockan szamolnak. Ez OBS-ben, ahol a bongeszo-forras amugy
 * is oszozik a kodolassal, lathato akadast okoz. A CSS keyframe ezzel szemben
 * a kompozitorra kerul: csak `transform` es `opacity` valtozik, ezekhez nem kell
 * ujrarajzolas.
 *
 * A kulcs-ertekek pontosan az eddigi mozgast masoljak: x 0 -> drift/2 -> drift,
 * y 0 -> 120vh, 720 fok forgas, az opacitas a vegen 0.3-ra esik.
 */
const CSS = `
.rain-drop {
  position: absolute;
  top: -50px;
  width: 40px;
  height: 40px;
  animation-name: rain-drop-fall;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  will-change: transform, opacity;
}

.rain-drop img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

@keyframes rain-drop-fall {
  0% {
    transform: translate3d(0, 0, 0) rotate(var(--rot));
    opacity: 1;
  }
  50% {
    transform: translate3d(calc(var(--drift) * 0.5), 60vh, 0)
      rotate(calc(var(--rot) + 360deg));
    opacity: 1;
  }
  100% {
    transform: translate3d(var(--drift), 120vh, 0)
      rotate(calc(var(--rot) + 720deg));
    opacity: 0.3;
  }
}
`;

const RainDropsComponent = forwardRef<RainDropsHandle, RainDropsProps>(
  ({ isActive, animationSpeed = 1 }, ref) => {
    const [rainDrops, setRainDrops] = useState<RainDrop[]>([]);
    const dropRefs = useRef<(HTMLDivElement | null)[]>([]);

    useImperativeHandle(ref, () => ({
      getPositions: () => {
        const container = dropRefs.current[0]?.parentElement;
        if (!container) return [];

        // A CSS transform is benne van a getBoundingClientRect-ben, tehat a
        // befagyasztott poziciok ugyanugy jonnek ki, mint a JS animacioval.
        const containerRect = container.getBoundingClientRect();
        return dropRefs.current
          .map((el, i) => {
            if (!el) return null;
            const rect = el.getBoundingClientRect();
            return {
              id: rainDrops[i]?.id || `drop-${i}`,
              x: ((rect.left - containerRect.left) / containerRect.width) * 100,
              y: ((rect.top - containerRect.top) / containerRect.height) * 100,
            };
          })
          .filter(Boolean) as FrozenDrop[];
      },
    }), [rainDrops]);

    useEffect(() => {
      if (!isActive) {
        setRainDrops([]);
        return;
      }

      setRainDrops(
        Array.from({ length: DROP_COUNT }, (_, i) => {
          const duration = 3 + Math.random() * 2;
          return {
            id: `drop-${i}`,
            x: Math.random() * 100,
            drift: (Math.random() - 0.5) * 80,
            // NEGATIV keslés: a csepp ugy indul, mintha mar esne egy ideje.
            // Ettol tolt ki azonnal a keret, utantoltes nelkul.
            delay: -Math.random() * duration,
            duration,
            rotation: Math.random() * 360,
          };
        }),
      );
    }, [isActive]);

    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <style>{CSS}</style>
        {rainDrops.map((drop, i) => (
          <div
            key={drop.id}
            ref={(el) => {
              dropRefs.current[i] = el;
            }}
            className="rain-drop"
            style={
              {
                left: `${drop.x}%`,
                "--drift": `${drop.drift}px`,
                "--rot": `${drop.rotation}deg`,
                animationDuration: `${drop.duration / animationSpeed}s`,
                animationDelay: `${drop.delay}s`,
              } as React.CSSProperties
            }
          >
            {/*
              Se `blur`, se `drop-shadow` a cseppen. Egy 40 px-es ikonon a
              0.5 px-es elmosas gyakorlatilag lathatatlan, viszont MINDEN
              csepp MINDEN kepkockan kulon rajzolasi kort kert tole -- 36-50
              elemnel ez volt a lag fo forrasa. A lagysagot a folotte levo
              sotetito reteg adja.
            */}
            <img src="/espont.png" alt="espont" />
          </div>
        ))}
      </div>
    );
  },
);

RainDropsComponent.displayName = "RainDrops";

export const RainDrops = RainDropsComponent;
